import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch";
import moment from "moment";
import { getSurveysService } from "../services/getSurveys.service";
import { exportExcelSurveysService } from "../services/exportExcelSurveys.service";
import { saveFile } from "../../../common/utils/saveFile.util";
import XLSX from "sheetjs-style";

export const useSurveys = () => {
  const fetchData = useFetch();
  const [surveys, setSurveys] = useState([]);
  const [errorFilters, setErrorFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);
  const [, setRefresh] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    groupQuestion: "",
    warning: "",
    placa: "",
    initialDate: moment().format("YYYY-MM-DD"),
    finalDate: moment().format("YYYY-MM-DD"),
  });

  const handleChangeFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const validateFilters = (isExport = false) => {
    const errors = {};

    if (filters.initialDate && filters.finalDate) {
      if (moment(filters.initialDate).isAfter(moment(filters.finalDate))) {
        errors.date =
          "La fecha de inicio no puede ser posterior a la fecha final.";
      }
    }

    if (!isExport && filters.groupQuestion === "") {
      errors.groupQuestion = "El grupo de preguntas es obligatorio.";
    }

    setErrorFilters(errors);
    return Object.keys(errors).length === 0;
  };

  const loadSurveys = async (internalFilters) => {
    try {
      setLoading(true);
      const isValid = validateFilters();
      if (!isValid) {
        setLoading(false);
        return;
      }
      const response = await getSurveysService(fetchData)({
        page,
        limit: 10,
        ...filters,
        ...internalFilters,
      });

      setSurveys(response.data);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      if(page > response.totalPages) {
        setPage(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Error loading surveys:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      setLoadingActions(true);
      const isValid = validateFilters(true);
      if (!isValid) {
        setLoadingActions(false);
        return;
      }

      const response = await exportExcelSurveysService(fetchData)(filters);
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const ws = XLSX.utils.json_to_sheet(response);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { booktype: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: fileType });
      const filename = `Encuesta_fecha_consulta${moment().format(
        "DD-MM-YYYY"
      )}_${moment().unix()}.xlsx`;
      saveFile(file, filename);
    } catch (error) {
      console.error("Error exporting surveys to Excel:", error);
    } finally {
      setLoadingActions(false);
    }
  };

  const exportSimpleExcel = async () => {
    try {
      setLoadingActions(true);
      const isValid = validateFilters(true);
      if (!isValid) {
        setLoadingActions(false);
        return;
      }

      const response = await exportExcelSurveysService(fetchData)(filters);
      const dataToExport = response.map((item) => ({
        FECHA: item.Fecha_registro,
        "NOMBRE DEL CONDUCTOR": item.Nombre,
        VEHICULO: item.Placa,
        "USUARIO QUE VERIFICA": item.usuarioaprobo,
        APROBADO: item.Estado === "Aprobado" ? "SI" : "NO",
      }));

      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { booktype: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: fileType });
      const filename = `Encuesta_fecha_consulta${moment().format(
        "DD-MM-YYYY"
      )}_${moment().unix()}.xlsx`;
      saveFile(file, filename);
    } catch (error) {
      console.error("Error exporting surveys to Excel:", error);
    } finally {
      setLoadingActions(false);
    }
  };

  return {
    surveys,
    loading,
    page,
    totalPages,
    total,
    filters,
    errorFilters,
    handleChangeFilters,
    onPage: (page) => setPage(() => page),
    refresh: () => setRefresh((prev) => !prev),
    loadSurveys,
    exportToExcel: exportExcel,
    exportSimpleExcel,
    loadingActions,
  };
};
